import { useCallback } from 'react';
import { SmartSendVersionFeatureModel } from '~/lib/api/me/types';
import { useMeApi } from '~/lib/api/me/use-me-api';
import { LocalStorageKeys } from '../smartsend/lib/consts/LocalStorageKeys';

const featureKey = 'SmartSendVersion';

const useSmartSendVersionFeature = () => {
  const { getUserSettings, upsertUserSetting } = useMeApi();

  const updateLocalStoargeValues = useCallback((preferredVersion: string, previousVersion: string) => {
    if (preferredVersion) {
      localStorage.setItem(LocalStorageKeys.SmartSendPreferredVersion, preferredVersion);
    }
    if (previousVersion) {
      localStorage.setItem(LocalStorageKeys.SmartSendPreviousPreferredVersion, previousVersion);
    }
  }, []);

  const getSummary = useCallback(async () => {
    try {
      const settings = await getUserSettings(featureKey);
      if (settings.success && settings.result) {
        const setting = settings.result.settings.find((s) => s.name === featureKey);
        if (setting?.value) {
          const value = setting.value as SmartSendVersionFeatureModel;
          updateLocalStoargeValues(value.PreferredVersion, value.PreviousVersion);
          return value;
        }
      }
    } catch (e) {
      console.error('Error setting preferred SmartSend version', e);
    }

    return null;
  }, [getUserSettings, updateLocalStoargeValues]);

  const setPreferredSmartSendVersion = useCallback(
    async (version: string) => {
      try {
        const setting = await getSummary();
        if (setting) {
          if (setting.PreferredVersion !== version) {
            const update = { ...setting, PreferredVersion: version, PreviousVersion: setting.PreferredVersion };
            await upsertUserSetting(featureKey, update);
            updateLocalStoargeValues(update.PreferredVersion, update.PreviousVersion);
          }
        }
        return true;
      } catch (e) {
        console.error('Error setting preferred SmartSend version', e);
      }
    },
    [getSummary, upsertUserSetting, updateLocalStoargeValues]
  );

  const getPreferredSmartSendVersion = useCallback(async () => {
    try {
      const setting = await getSummary();
      if (setting) {
        return setting.PreferredVersion;
      }
    } catch (e) {
      console.error('Error setting preferred SmartSend version', e);
    }
    return null;
  }, [getSummary]);

  const revertPreferredSmartSendVersion = useCallback(async () => {
    try {
      const setting = await getSummary();
      if (setting) {
        const update = {
          ...setting,
          PreferredVersion: setting.PreviousVersion,
          PreviousVersion: setting.PreferredVersion
        };
        await upsertUserSetting(featureKey, update);
        updateLocalStoargeValues(update.PreferredVersion, update.PreviousVersion);
      }
      return true;
    } catch (e) {
      console.error('Error setting preferred SmartSend version', e);
    }
  }, [getSummary, upsertUserSetting, updateLocalStoargeValues]);

  return {
    getPreferredSmartSendVersion,
    revertPreferredSmartSendVersion,
    setPreferredSmartSendVersion,
    getSummary
  } as const;
};

export default useSmartSendVersionFeature;
